import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { AnswersIds, RegisterData } from "@/Modules/Models/Domain/OnBoarding";
import type { RegisterUserApiQuery } from "@/Modules/Models/Api/OnBoarding";
import type { Language } from "@/Common/Models/Domain/Language";
import { TherapyType, RegisterEvent, YesOrNoAnswer } from "@/Modules/Models/Domain/OnBoarding";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";

import useUserStore from "@/Modules/User/Store";
import { useAuthStore } from "@/Modules/Auth/Store";

import OnBoardingAPIConnector from "@/Services/TherapyApi/OnBoarding";

import { danger } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";

import { initLoguedAppGuard } from "@/Router/guards";

import { isApiError } from "@/Services/Api/Typeguards/isApiError";
import { isValidString } from "@/Utils/String";

const ensureValue = <V, D>(value: V, defValue: D): V | D => {
  if (!hasValue(value)) return defValue;

  return value;
};

class RegisterUser {
  constructor(
    private readonly onBoardingStore: OnBoardingStore,
    private readonly authStore = useAuthStore(),
    private readonly userStore = useUserStore(),
    private readonly onBoardingApiConnector: OnBoardingAPIConnector = new OnBoardingAPIConnector()
  ) {}

  public async handle(registerData: RegisterData, language: string) {
    const onBoardingData = this.onBoardingStore.answers;
    const apiQuery = this.transformDataToApiQuery(onBoardingData, registerData);

    this.onBoardingStore.hasErrorInProcess = false;
    this.onBoardingStore.errorCode = null;
    this.onBoardingStore.isLoading = true;

    try {
      const registerResponse = await this.onBoardingApiConnector.registerUser(apiQuery);

      this.authStore.setUserToken(registerResponse.token);

      await initLoguedAppGuard();

      if (hasValue(registerResponse.source)) {
        const b2bUser = {
          source: registerResponse.source,
          language: language,
          email: registerData.email,
          codeType: this.onBoardingStore.b2bUser?.codeType,
        };

        this.onBoardingStore.setB2BData(b2bUser);
        this.onBoardingStore.setOptionsData(language as Language);
      }

      await this.userStore.setUser();

      this.onBoardingStore.sendGTMEvent({
        language: language,
        registerData,
        eventStep: RegisterEvent.OK,
      });
      this.onBoardingStore.setNextStep(null);
    } catch (err: unknown) {
      const code = isApiError(err) ? err["error_code"] : null;

      this.onBoardingStore.hasErrorInProcess = true;

      if (code === ApiErrorCodes.INVALID_EMAIL) {
        this.onBoardingStore.errorCode = ApiErrorCodes.INVALID_EMAIL;
      }

      this.onBoardingStore.sendGTMEvent({
        language: language,
        registerData,
        eventStep: RegisterEvent.KO,
      });
      danger(err);
    } finally {
      this.onBoardingStore.isLoading = false;
    }
  }

  private transformDataToApiQuery(
    onBoardingData: Partial<Record<AnswersIds, string>>,
    registerData: RegisterData
  ): RegisterUserApiQuery {
    const language = hasValue(onBoardingData.language) ? onBoardingData.language : "";
    const therapyLanguage = hasValue(onBoardingData.therapy_language)
      ? onBoardingData.therapy_language
      : undefined;

    return {
      username: ensureValue(onBoardingData.username, "") as string,
      language: language as Language,
      additional_language: ensureValue(therapyLanguage, undefined),
      is_couple: onBoardingData.therapy_type === TherapyType.COUPLE,
      age_detailed: ensureValue(onBoardingData.user_age, undefined),
      gender: ensureValue(onBoardingData.user_gender, undefined),
      couple_age_detailed: ensureValue(onBoardingData.couple_age, undefined),
      couple_gender: ensureValue(onBoardingData.couple_gender, undefined),
      patient_subarea: ensureValue(onBoardingData.sub_area, "") as string,
      tell_me_more: ensureValue(onBoardingData.tell_me_more, undefined),
      gender_preference: ensureValue(onBoardingData.therapist_gender_preferences, undefined),
      availability: ensureValue(onBoardingData.therapist_availability, undefined),
      full_name: registerData.fullName,
      email: registerData.email,
      password: registerData.password,
      contact_phone: isValidString(registerData.contactPhone) ? registerData.contactPhone : undefined,
      is_want_trial: registerData.isWantTrial,
      newsletter: registerData.newsLetter,
      therapist_name: ensureValue(onBoardingData.therapist_name, undefined),
      first_time: onBoardingData.first_time === YesOrNoAnswer.YES,
      code_type: isValidString(registerData.codeType) ? registerData.codeType : undefined,
      privacy_policy_at: registerData.privacyPolicyAt,
    };
  }
}

export default RegisterUser;
